/**
 * exportData — declared export profiles (task 35.01 §4, Tier 2).
 *
 * Read by both sides: the server resolves the profile named in the request, the
 * client lists the available profiles in the export dialog.
 *
 * Nothing here is required. An entity with no entry still exports — the server
 * derives a profile from its AJV schema (Tier 1), so this map exists only to
 * curate: to choose the columns, order them, and label them for a reader. A
 * request may also carry its own `columns`, which beats both (Tier 3).
 *
 * Per field:
 *   field     path on the row — 'name', or a path like 'customer.name'
 *   label     translation key; the client resolves it and sends the header text
 *   type      text | amount | number | integer | date | datetime | boolean
 *   decimals  amount and number only
 *
 * A reference exports as two fields, id and name side by side (§11.2): the id is
 * what a re-import needs, the name is what a reader needs, and the name is
 * already on the row as a denormalized `…Name` field.
 */
export const exportData = {
    expense: {
        default: {
            label: 'purchases.expense.export.default',
            granularity: 'header',
            fields: [
                { field: 'id',                         label: 'purchases.expense.export.id',                  type: 'text' },
                { field: 'bookedNumber',               label: 'purchases.expense.list.table.number',          type: 'text' },
                { field: 'supplierInvoiceNumber',      label: 'purchases.expense.list.table.supplierInvoice', type: 'text' },
                { field: 'date',                       label: 'purchases.expense.list.table.date',            type: 'date' },
                { field: 'state',                      label: 'purchases.expense.export.state',               type: 'text' },
                { field: 'supplierId',                 label: 'purchases.expense.export.supplierId',          type: 'text' },
                { field: 'supplierName',               label: 'purchases.expense.list.table.supplier',        type: 'text' },
                { field: 'purchaseOrderNumber',        label: 'purchases.expense.export.purchaseOrder',       type: 'text' },
                { field: 'totalNetAmountBaseCurrency', label: 'purchases.expense.export.netAmount',           type: 'amount', decimals: 2 },
                { field: 'totalGrossAmount',           label: 'purchases.expense.list.table.totalAmount',     type: 'amount', decimals: 2 },
                { field: 'currency',                   label: 'purchases.expense.export.currency',            type: 'text' },
                { field: 'paymentDate',                label: 'purchases.expense.list.table.paymentDate',     type: 'date' },
                { field: 'paid',                       label: 'purchases.expense.export.paid',                type: 'boolean' }
            ]
        }
    },

    /*
     * The three sales/purchase documents export their header only: totals,
     * parties, dates and state. Lines are a `granularity: 'lines'` profile if
     * anyone needs them (§11.1 of 35.02), not something to smuggle into a header
     * export as a JSON blob in a cell.
     *
     * These carry `bookedNumber` rather than `number`: the working number is a
     * draft that changes at booking, the booked one is what the outside world
     * has seen.
     */
    invoice: {
        default: {
            label: 'invoice.export.default',
            granularity: 'header',
            fields: [
                { field: 'id',                    label: 'invoice.export.id',              type: 'text' },
                { field: 'bookedNumber',          label: 'invoice.list.table.number',      type: 'text' },
                { field: 'date',                  label: 'invoice.list.table.date',        type: 'date' },
                { field: 'state',                 label: 'invoice.export.state',           type: 'text' },
                { field: 'customerId',            label: 'invoice.export.customerId',      type: 'text' },
                { field: 'customerName',          label: 'invoice.list.table.customerName', type: 'text' },
                { field: 'totalNetSalesAmount',   label: 'invoice.export.netAmount',       type: 'amount', decimals: 2 },
                { field: 'totalVatTaxAmount',     label: 'invoice.export.vatAmount',       type: 'amount', decimals: 2 },
                { field: 'totalGrossSalesAmount', label: 'invoice.list.table.amount',      type: 'amount', decimals: 2 },
                { field: 'currency',              label: 'invoice.export.currency',        type: 'text' },
                { field: 'paymentDate',           label: 'invoice.export.paymentDate',     type: 'date' },
                { field: 'paid',                  label: 'invoice.list.table.paid',        type: 'boolean' }
            ]
        }
    },

    /*
     * A subcollection profile, and the first one. Nothing about it is special:
     * the profile is looked up by entity name, and a subcollection has one like
     * anything else.
     *
     * What it leaves out is the point. `amountBaseCurrency`, the sign flags and
     * every amountLeft/amountPaid/fx field are computed by logicData on write
     * (coreDb runs it) or by the matching engine, so they are marked
     * `import: false` in the schema and appear here as read-only columns — a
     * reader wants the base-currency amount, and a file cannot set it.
     *
     * `exchangeRate` is the exception and is why it is here: it comes from a
     * field trigger in the form (getExchangeRate), which no import path runs, so
     * the file is the only thing that can supply it. Left out, it defaults to 1
     * and a foreign-currency account converts at par.
     *
     * `transactionId` is the bank's own reference and the entity's importKey —
     * the column that stops a re-imported statement from booking every movement
     * twice. Dropping it from the file gives that up.
     */
    paymentTransaction: {
        default: {
            label: 'bank.account.transaction.export.default',
            granularity: 'header',
            fields: [
                { field: 'id',                 label: 'bank.account.transaction.export.id',                type: 'text' },
                { field: 'date',               label: 'bank.account.transaction.list.table.date',          type: 'date' },
                { field: 'description',        label: 'bank.account.transaction.list.table.description',   type: 'text' },
                { field: 'amount',             label: 'bank.account.transaction.list.table.amount',        type: 'amount', decimals: 2 },
                { field: 'currency',           label: 'bank.account.transaction.form.currency',            type: 'text' },
                { field: 'exchangeRate',       label: 'bank.account.transaction.export.exchangeRate',      type: 'number', decimals: 6 },
                { field: 'amountBaseCurrency', label: 'bank.account.transaction.export.amountBaseCurrency', type: 'amount', decimals: 2 },
                { field: 'transactionType',    label: 'bank.account.transaction.export.transactionType',   type: 'text' },
                { field: 'counterparty',       label: 'bank.account.transaction.export.counterparty',      type: 'text' },
                { field: 'reference',          label: 'bank.account.transaction.export.reference',         type: 'text' },
                { field: 'transactionId',      label: 'bank.account.transaction.export.transactionId',     type: 'text' },
                { field: 'paymentAccountId',   label: 'bank.account.transaction.export.accountId',         type: 'text' },
                { field: 'accountName',        label: 'bank.account.transaction.export.accountName',       type: 'text' },
                { field: 'fullyMatched',       label: 'bank.account.transaction.list.table.matched',       type: 'boolean' }
            ]
        }
    },

    product: {
        default: {
            label: 'product.export.default',
            granularity: 'header',
            fields: [
                // Carried so the file can come back: an export without the key is
                // not round-trippable, and every row of it re-imports as an insert
                // (35.02 §2.3). Delete the column and matching falls back to sku.
                { field: 'id',             label: 'product.export.id',              type: 'text' },
                { field: 'sku',            label: 'product.list.table.sku',         type: 'text' },
                { field: 'name',           label: 'product.list.table.name',        type: 'text' },
                { field: 'description',    label: 'product.list.table.description', type: 'text' },
                { field: 'type',           label: 'product.list.table.type',        type: 'text' },
                { field: 'familyId',       label: 'product.export.familyId',        type: 'text' },
                { field: 'familyName',     label: 'product.form.family',            type: 'text' },
                { field: 'netSalesPrice',  label: 'product.list.table.netSalesPrice', type: 'amount', decimals: 2 },
                { field: 'currency',       label: 'product.export.currency',        type: 'text' },
                { field: 'unitId',         label: 'product.export.unitId',          type: 'text' },
                { field: 'unitName',       label: 'product.form.unitName',          type: 'text' },
                { field: 'totalInventory', label: 'product.export.totalInventory',  type: 'number', decimals: 2 },
                { field: 'active',         label: 'product.export.active',          type: 'boolean' },
                { field: 'productGroup',         label: 'product.export.productGroup',          type: 'text' }
            ]
        },
        pricing: {
            label: 'product.export.pricing',
            granularity: 'header',
            fields: [
                { field: 'id',               label: 'product.export.id',                type: 'text' },
                { field: 'sku',              label: 'product.list.table.sku',           type: 'text' },
                { field: 'name',             label: 'product.list.table.name',          type: 'text' },
                { field: 'netSalesPrice',    label: 'product.list.table.netSalesPrice', type: 'amount', decimals: 2 },
                { field: 'grossSalesPrice',  label: 'product.list.table.grossSalesPrice', type: 'amount', decimals: 2 },
                { field: 'taxRate',          label: 'product.export.taxRate',           type: 'number', decimals: 2 },
                { field: 'unitCost',         label: 'product.export.unitCost',          type: 'amount', decimals: 2 },
                { field: 'currency',         label: 'product.export.currency',          type: 'text' }
            ]
        }
    },

    /*
     * productGroup keys on `id` like everything else, but here the id is a real
     * schema property the user types — a code such as "TOYS" — rather than a
     * generated ULID. So it needs no importKey: the key is already meaningful,
     * already in the file, and already what a person would edit.
     */
    productGroup: {
        default: {
            label: 'productGroup.export.default',
            granularity: 'header',
            fields: [
                { field: 'id',          label: 'productGroup.form.code',        type: 'text' },
                { field: 'description', label: 'productGroup.form.description', type: 'text' },
                { field: 'type',        label: 'productGroup.form.type',        type: 'text' },
                { field: 'parentId',    label: 'productGroup.form.parentId',    type: 'text' },
                { field: 'sortKey',     label: 'productGroup.export.sortKey',   type: 'text' }
            ]
        }
    },

    purchaseOrder: {
        default: {
            label: 'purchases.purchaseOrder.export.default',
            granularity: 'header',
            fields: [
                { field: 'id',                       label: 'purchases.purchaseOrder.export.id',           type: 'text' },
                { field: 'bookedNumber',             label: 'purchases.purchaseOrder.list.table.number',   type: 'text' },
                { field: 'date',                     label: 'purchases.purchaseOrder.list.table.date',     type: 'date' },
                { field: 'deliveryDate',             label: 'purchases.purchaseOrder.list.table.deliveryDate', type: 'date' },
                { field: 'state',                    label: 'purchases.purchaseOrder.list.table.state',    type: 'text' },
                { field: 'supplierId',               label: 'purchases.purchaseOrder.export.supplierId',   type: 'text' },
                { field: 'supplierName',             label: 'purchases.purchaseOrder.list.table.supplier', type: 'text' },
                { field: 'totalNetAmountBaseCurrency', label: 'purchases.purchaseOrder.list.table.totalAmount', type: 'amount', decimals: 2 },
                { field: 'currency',                 label: 'purchases.purchaseOrder.export.currency',     type: 'text' },
                { field: 'paymentDate',              label: 'purchases.purchaseOrder.export.paymentDate',  type: 'date' },
                { field: 'paid',                     label: 'purchases.purchaseOrder.export.paid',         type: 'boolean' }
            ]
        }
    },

    quote: {
        default: {
            label: 'quote.export.default',
            granularity: 'header',
            fields: [
                { field: 'id',                    label: 'quote.export.id',              type: 'text' },
                { field: 'bookedNumber',          label: 'quote.list.table.number',      type: 'text' },
                { field: 'date',                  label: 'quote.list.table.date',        type: 'date' },
                { field: 'state',                 label: 'quote.export.state',           type: 'text' },
                { field: 'customerId',            label: 'quote.export.customerId',      type: 'text' },
                { field: 'customerName',          label: 'quote.list.table.customerName', type: 'text' },
                { field: 'totalNetSalesAmount',   label: 'quote.export.netAmount',       type: 'amount', decimals: 2 },
                { field: 'totalVatTaxAmount',     label: 'quote.export.vatAmount',       type: 'amount', decimals: 2 },
                { field: 'totalGrossSalesAmount', label: 'quote.list.table.amount',      type: 'amount', decimals: 2 },
                { field: 'currency',              label: 'quote.export.currency',        type: 'text' }
            ]
        }
    }
}

export default exportData
